import React from 'react';
import { format, isSameMonth, isSameYear, isSameDay, isValid } from 'date-fns';
import Box from '@Atoms/Box/Box';
import Typography from '@Atoms/Typography/Typography';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { alpha, SxProps } from '@mui/material/styles';
import { Theme } from '@mui/material';
import { formatDateWithTimezone, parseCalendarDate } from '@utils/datetime';
import { sxCombine } from '../utiles';
import ShowIf from '../ShowIf';

export interface DateDisplayProps {
  startDate: Date | string;
  endDate?: Date | string;
  showIcon?: boolean;
  variant?: 'default' | 'compact' | 'detailed' | 'elegant' | 'minimal';
  size?: 'small' | 'medium' | 'large';
  className?: string;
  sx?: SxProps<Theme>;
  color?:
    | 'primary'
    | 'secondary'
    | 'info'
    | 'success'
    | 'warning'
    | 'error'
    | 'subtitle'
    | 'default';
  showTime?: boolean;
  placeholder?: string;
  showTimezone?: boolean;
}
const SingleDate = ({ getVariantStyles, start, formatDate, showTime, showTimezone }) => (
  <>
    <ShowIf If={showTimezone}>
      <Typography variant="body2" sx={getVariantStyles().text}>
        {formatDateWithTimezone(start)}
      </Typography>
    </ShowIf>
    <ShowIf If={!showTimezone}>
      <Typography variant="body2" sx={getVariantStyles().text}>
        {formatDate(start, 'MMM dd, yyyy')}
      </Typography>
      {showTime && (
        <Typography variant="body2" sx={getVariantStyles().text}>
          {formatDate(start, 'hh:mm a')}
        </Typography>
      )}
    </ShowIf>
  </>
);

const DateRange = ({
  getVariantStyles,
  end,
  sameDay,
  formatDate,
  start,
  sameMonth,
  sameYear,
  showTime,
  showTimezone,
}) => {
  if (!end) {
    return (
      <SingleDate
        getVariantStyles={getVariantStyles}
        start={start}
        formatDate={formatDate}
        showTime={showTime}
        showTimezone={showTimezone}
      />
    );
  }

  // Same day - show single date
  if (sameDay) {
    return (
      <Typography variant="body2" sx={getVariantStyles().text}>
        {formatDate(start, 'MMM dd, yyyy')}
      </Typography>
    );
  }

  // Same month and year - show "Jan 15-20, 2024"
  if (sameMonth && sameYear) {
    return (
      <Typography variant="body2" sx={getVariantStyles().text}>
        {formatDate(start, 'MMM dd')} - {formatDate(end, 'dd, yyyy')}
      </Typography>
    );
  }

  // Same year but different months - show "Jan 15 - Feb 20, 2024"
  if (sameYear) {
    return (
      <Typography variant="body2" sx={getVariantStyles().text}>
        {formatDate(start, 'MMM dd')} - {formatDate(end, 'MMM dd, yyyy')}
      </Typography>
    );
  }

  // Different years - show full dates "Jan 15, 2024 - Feb 20, 2025"
  return (
    <Typography variant="body2" sx={getVariantStyles().text}>
      {formatDate(start, 'MMM dd, yyyy')} - {formatDate(end, 'MMM dd, yyyy')}
    </Typography>
  );
};
const DateDisplay: React.FC<DateDisplayProps> = ({
  startDate,
  endDate,
  showIcon = false,
  variant = 'default',
  size = 'medium',
  className,
  sx,
  color = 'default',
  showTime = false,
  placeholder = '--',
  showTimezone = false,
}) => {
  const start =
    typeof startDate === 'string'
      ? parseCalendarDate(startDate) ?? new Date(NaN)
      : startDate;
  let end: Date | null = null;
  if (endDate) {
    end = typeof endDate === 'string' ? parseCalendarDate(endDate) : endDate;
  }

  const isRange = !!end;
  const sameYear = isRange && end ? isSameYear(start, end) : false;
  const sameMonth = isRange && end ? isSameMonth(start, end) : false;
  const sameDay = isRange && end ? isSameDay(start, end) : false;

  const getColorScheme = () => {
    switch (color) {
      case 'primary':
        return {
          icon: '#1976d2',
          text: '#1976d2',
          bg: alpha('#1976d2', 0.08),
          border: alpha('#1976d2', 0.2),
        };
      case 'secondary':
        return {
          icon: '#9c27b0',
          text: '#9c27b0',
          bg: alpha('#9c27b0', 0.08),
          border: alpha('#9c27b0', 0.2),
        };
      case 'info':
        return {
          icon: '#0288d1',
          text: '#0288d1',
          bg: alpha('#0288d1', 0.08),
          border: alpha('#0288d1', 0.2),
        };
      case 'success':
        return {
          icon: '#2e7d32',
          text: '#2e7d32',
          bg: alpha('#2e7d32', 0.08),
          border: alpha('#2e7d32', 0.2),
        };
      case 'warning':
        return {
          icon: '#ed6c02',
          text: '#ed6c02',
          bg: alpha('#ed6c02', 0.08),
          border: alpha('#ed6c02', 0.2),
        };
      case 'error':
        return {
          icon: '#C62828',
          text: '#C62828',
          bg: alpha('#C62828', 0.08),
          border: alpha('#C62828', 0.2),
        };
      case 'subtitle':
        return {
          icon: '#6c757d',
          text: '#666666',
          bg: '#f8f9fa',
          border: '#e9ecef',
        };
      default:
        return {
          icon: '#6c757d',
          text: '#495057',
          bg: '#f8f9fa',
          border: '#e9ecef',
        };
    }
  };

  const getSizeStyles = () => {
    switch (size) {
      case 'small':
        return {
          iconSize: '14px',
          fontSize: '0.75rem',
          gap: '4px',
          padding: '4px 8px',
          borderRadius: '4px',
        };
      case 'large':
        return {
          iconSize: '20px',
          fontSize: '1rem',
          gap: '10px',
          padding: '12px 16px',
          borderRadius: '10px',
        };
      default:
        // medium
        return {
          iconSize: '16px',
          fontSize: '0.875rem',
          gap: '6px',
          padding: '6px 10px',
          borderRadius: '6px',
        };
    }
  };

  const getVariantStyles = (): {
    container: SxProps<Theme>;
    text: SxProps<Theme>;
  } => {
    const colorScheme = getColorScheme();
    const sizeStyles = getSizeStyles();

    switch (variant) {
      case 'minimal':
        return {
          container: {
            display: 'flex',
            alignItems: 'center',
            gap: sizeStyles.gap,
          },
          text: {
            color: colorScheme.text,
            fontSize: sizeStyles.fontSize,
            fontWeight: 400,
          },
        };
      case 'compact':
        return {
          container: {
            display: 'flex',
            alignItems: 'center',
            gap: sizeStyles.gap,
            padding: sizeStyles.padding,
            backgroundColor: alpha(colorScheme.bg, 0.3),
            borderRadius: sizeStyles.borderRadius,
          },
          text: {
            color: colorScheme.text,
            fontSize: sizeStyles.fontSize,
            fontWeight: 500,
          },
        };
      case 'elegant':
        return {
          container: {
            display: 'flex',
            alignItems: 'center',
            gap: sizeStyles.gap,
            padding: sizeStyles.padding,
            backgroundColor: colorScheme.bg,
            borderRadius: sizeStyles.borderRadius,
            border: `1px solid ${colorScheme.border}`,
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          },
          text: {
            color: colorScheme.text,
            fontSize: sizeStyles.fontSize,
            fontWeight: 600,
          },
        };
      case 'detailed':
        return {
          container: {
            display: 'flex',
            alignItems: 'center',
            gap: sizeStyles.gap,
            padding: sizeStyles.padding,
            backgroundColor: colorScheme.bg,
            borderRadius: sizeStyles.borderRadius,
            border: `2px solid ${colorScheme.border}`,
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '2px',
              background: `linear-gradient(90deg, ${colorScheme.icon}, ${alpha(
                colorScheme.icon,
                0.6,
              )})`,
              borderRadius: `${sizeStyles.borderRadius}px ${sizeStyles.borderRadius}px 0 0`,
            },
          },
          text: {
            color: colorScheme.text,
            fontSize: sizeStyles.fontSize,
            fontWeight: 600,
          },
        };
      default:
        // default
        return {
          container: {
            display: 'flex',
            alignItems: 'center',
            gap: sizeStyles.gap,
            padding: sizeStyles.padding,
            backgroundColor: colorScheme.bg,
            borderRadius: sizeStyles.borderRadius,
            border: `1px solid ${colorScheme.border}`,
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              backgroundColor: alpha(colorScheme.bg, 1),
              borderColor: alpha(colorScheme.border, 1),
            },
          },
          text: {
            color: colorScheme.text,
            fontSize: sizeStyles.fontSize,
            fontWeight: 500,
          },
        };
    }
  };

  const formatDate = (date: Date, formatString: string) => format(date, formatString);

  const styles = getVariantStyles();
  const colorScheme = getColorScheme();

  return (
    <Box className={className} sx={sxCombine(styles.container, sx ?? {})}>
      {showIcon && (
        <CalendarTodayIcon
          sx={{
            fontSize: getSizeStyles().iconSize,
            color: colorScheme.icon,
            opacity: variant === 'minimal' ? 0.7 : 1,
          }}
        />
      )}
      <ShowIf If={!isValid(start)}>
        <Typography variant="body2" sx={getVariantStyles().text}>
          {placeholder}
        </Typography>
      </ShowIf>
      <ShowIf If={isValid(start)}>
        {isRange ? (
          <DateRange
            getVariantStyles={getVariantStyles}
            end={end}
            sameDay={sameDay}
            formatDate={formatDate}
            start={start}
            sameMonth={sameMonth}
            sameYear={sameYear}
            showTime={undefined}
            showTimezone={showTimezone}
          />
        ) : (
          <SingleDate
            getVariantStyles={getVariantStyles}
            start={start}
            formatDate={formatDate}
            showTime={showTime}
            showTimezone={showTimezone}
          />
        )}
      </ShowIf>
    </Box>
  );
};

export default DateDisplay;
